import { ref, get, update, query, orderByChild, startAt, equalTo, limitToFirst } from 'firebase/database';
import { db } from '../../../integrations/firebase';
import { authHeaders } from '../../../core/api/authHeaders';
import { RECON, resolveGateway, isCOD } from './cashfreeReconcile';
import { AUTO_CHECK_GIVE_UP_MS } from '../logic/autoVerifyCadence';

// Auth-injecting fetch for the (protected) payment proxy endpoints.
const pfetch = async (url, opts = {}) => fetch(url, { ...opts, headers: { ...(opts.headers || {}), ...(await authHeaders()) } });

// Cashfree credentials live server-side (server/.env); the browser only sends order IDs.
// Cashfree is the ONLY supported gateway. Razorpay was removed in D1A.
const PROXY_BASE = (import.meta.env.VITE_PROXY_URL || (import.meta.env.DEV ? 'http://localhost:3001' : '')).replace(/\/$/, '');


// Cadence + give-up window live in pure logic (see logic/autoVerifyCadence.js).

// 503 from the proxy means Cashfree is not configured server-side.
const isNotConfigured = (response) => response && response.status === 503;


export const PaymentReconciliationService = {
    /**
     * Reconcile one order by its ERP id. Cashfree-only; COD and legacy/unknown
     * gateways short-circuit without any gateway call.
     */
    reconcileSingleOrder: async (orderId, force = false) => {
        try {
            // Find the one order over the indexed `id` child (with a direct key
            // get first — storefront/website orders are keyed by their id).
            // This used to download the ENTIRE orders tree into the browser on
            // every "Verify Now" click.
            let firebaseKey = null;
            let order = null;
            if (/^[A-Za-z0-9_-]{1,60}$/.test(String(orderId))) {
                const direct = await get(ref(db, `orders/${orderId}`));
                if (direct.exists()) { firebaseKey = orderId; order = direct.val(); }
            }
            if (!order) {
                const s = await get(query(ref(db, 'orders'), orderByChild('id'), equalTo(String(orderId)), limitToFirst(1)));
                if (s.exists()) {
                    const [k, v] = Object.entries(s.val())[0];
                    firebaseKey = k; order = v;
                }
            }
            if (!order) return { success: false, error: 'Order not found' };

            const gw = resolveGateway(order);
            if (gw.type === 'cod') {
                console.log(`[Reconciliation] ${orderId}: COD → skipped (no gateway call).`);
                return { success: true, status: RECON.COD_SKIPPED };
            }
            if (gw.type === 'unsupported') {
                console.warn(`[Reconciliation] ${orderId}: unsupported gateway (${gw.reason}) → manual review.`);
                return { success: false, status: RECON.UNSUPPORTED_GATEWAY, reason: gw.reason };
            }
            return await PaymentReconciliationService._processCashfreeOrder(firebaseKey, order, force);
        } catch (error) {
            console.error('[Reconciliation] Single Sync Error:', error);
            return { success: false, error: error.message };
        }
    },

    /**
     * Cashfree verification, done by the proxy (it asks Cashfree, applies the PURE
     * verdict and writes the payment). Never touches COD, never calls any Razorpay
     * endpoint.
     */
    _processCashfreeOrder: async (key, order, force = false) => {
        const now = Date.now();
        const payment = order.payment || {};

        // Safety guard (should already be filtered by resolveGateway).
        if (isCOD(order)) return { success: true, status: RECON.COD_SKIPPED };

        if (!force && payment.nextCheckAfter && now < payment.nextCheckAfter) {
            return { success: true, status: 'throttled' };
        }

        try {
            // 1-3. The PROXY asks Cashfree, applies the same pure verdict
            // (server/lib/cashfreeReconcile.js mirrors ./cashfreeReconcile.js) and writes
            // the payment fields. A browser may no longer write PAID itself — the database
            // refuses it (security review, 1 Nov 2026) — so the desk only reports here.
            const r = await pfetch(`${PROXY_BASE}/api/orders/${encodeURIComponent(key)}/payment/cashfree-verify`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ force: !!force }),
            });
            if (isNotConfigured(r)) return { success: false, error: 'Cashfree not configured on server' };
            const out = await r.json().catch(() => ({ success: false, error: `HTTP ${r.status}` }));
            if (!out.success) return out;

            // 4. What is left for the desk: the order's own status.
            switch (out.status) {
                case RECON.PAID: {
                    // Promote the order once the money is proven (idempotent — only from
                    // a pre-payment status). 'Payment Pending'/'Payment Failed' belong
                    // here too: a storefront order waits at 'Payment Pending' until the
                    // CUSTOMER returns to the result page, and if they paid but never
                    // came back, the desk showed PAID + PAYMENT PENDING side by side
                    // forever. A proven PAID also outranks an earlier 'Payment Failed'.
                    const cur = await get(ref(db, `orders/${key}/status`));
                    const st = cur.exists() ? String(cur.val()).toLowerCase() : '';
                    if (['pending', 'payment pending', 'payment failed'].includes(st)) {
                        let finalized = false;
                        if (order.source === 'storefront' && order.customer?.mobile) {
                            // The server finalizer also consumes held reward points and
                            // applies stock/customer sync — things the desk cannot do.
                            try {
                                const r = await fetch(`${PROXY_BASE}/api/public/shop/order?oid=${encodeURIComponent(key)}&m=${encodeURIComponent(order.customer.mobile)}`);
                                if (r.ok) {
                                    const j = await r.json().catch(() => ({}));
                                    finalized = j?.paid === true;
                                }
                            } catch { /* fall through to the plain promotion below */ }
                        }
                        if (!finalized) await update(ref(db, `orders/${key}`), { status: 'Processed' });
                    }
                    break;
                }
                // Every other verdict is recorded on the payment by the proxy.
                default:
                    break;
            }
            return out;
        } catch (err) {
            console.error(`[Reconciliation] Cashfree error for ${order.id}:`, err);
            return { success: false, error: err.message };
        }
    },

    /**
     * Background loop: website orders in the last 14 days. COD and legacy/unknown
     * gateways are skipped WITHOUT any gateway call; already-PAID Cashfree orders
     * are skipped (idempotent). No gateway probing.
     */
    reconcile: async (force = false) => {
        try {
            const now = Date.now();
            // Anything older than the give-up window is no longer auto-checked. We fetch a
            // day past it so orders that have just crossed the line get stamped once
            // instead of silently vanishing from the loop.
            const windowStart = new Date(now - AUTO_CHECK_GIVE_UP_MS - 24 * 60 * 60 * 1000).toISOString();
            const snap = await get(query(ref(db, 'orders'), orderByChild('createdAt'), startAt(windowStart)));
            if (!snap.exists()) return;
            const orders = snap.val();

            let checked = 0; let gaveUp = 0;
            for (const key in orders) {
                const order = orders[key];
                if (order.source !== 'website') continue;

                const gw = resolveGateway(order);
                if (gw.type === 'cod') continue;            // COD never enters prepaid verification
                if (gw.type === 'unsupported') continue;    // legacy Razorpay / unknown → manual review, no call

                // Idempotent: don't re-hit Cashfree for an order already verified PAID.
                if (String(order.payment?.verificationStatus || '').toUpperCase() === 'PAID') continue;

                // Unpaid for more than 3 days → stop checking automatically. Stamped once
                // so the desk can say so; "Verify Now" still forces a live lookup.
                const age = now - (Date.parse(order.createdAt || '') || now);
                if (age > AUTO_CHECK_GIVE_UP_MS) {
                    if (!order.payment?.autoCheckStoppedAt) {
                        await update(ref(db, `orders/${key}/payment`), { autoCheckStoppedAt: now });
                        gaveUp += 1;
                    }
                    continue;
                }

                await PaymentReconciliationService._processCashfreeOrder(key, order, force);
                checked += 1;
            }
            if (checked || gaveUp) console.log(`[Reconciliation] auto pass: ${checked} checked, ${gaveUp} stopped (unpaid > 3 days)`);
        } catch (error) {
            console.error('[Reconciliation] Loop Error:', error);
        }
    },
};
